<script setup>
import { computed, ref, watch } from 'vue'
import { useIpTableSettings } from '@/composables/useIpTableSettings'
import { useTableExport } from '@/composables/useTableExport'
import { getAvailableDescriptors, getValue } from '@/config/ipTableFields'
import { useCellSelection } from '@/composables/useCellSelection'
import { deepContainsValue } from '@/utils/selection'
import IpResultsTable from './IpResultsTable.vue'
import IpTableSidebar from './IpTableSidebar.vue'

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  pendingSideload: {
    type: Set,
    default: () => new Set()
  }
})

const fieldSettings = useIpTableSettings()

// Enabled fields, ordered. The persisted order covers registry descriptors;
// sideloaded enrichment slugs (granted but unknown to the registry) appear in
// the data but not in the saved order, so append any that are enabled.
const enabledFields = computed(() => {
  const ordered = fieldSettings.getOrderedEnabledFields()
  const inOrder = new Set(ordered)
  const extra = getAvailableDescriptors(props.data)
    .map(d => d.id)
    .filter(id => !inOrder.has(id) && fieldSettings.isFieldEnabled(id))
  return [...ordered, ...extra]
})

// Filtering is owned here: the table emits intents, this applies them to the
// filter state and hands back the filtered rows. Swap these for an API call to
// move filtering server-side.
const sel = useCellSelection()
const appliedData = computed(() => sel.filterRows(props.data, getValue, deepContainsValue))
function onApplyFilter(items, operator) { sel.applyOrToggle(items, operator) }
function onToggleContains(field, value, negate) { sel.toggleContains(field, value, negate) }
function onRemoveFilter(id) { sel.removeApplied(id) }

// Text-searched/sorted view as reported by IpResultsTable. Drives the sidebar
// count and export so both reflect what the user actually sees.
const filteredData = ref(props.data)
watch(() => props.data, (v) => { filteredData.value = v })
function handleFilteredChange(rows) {
  filteredData.value = rows
}

// Export (shared with the Selector Search tool rail via useTableExport).
const { exportRows } = useTableExport()

function handleExport(format) {
  exportRows(filteredData.value, format, 'ip-results')
}
</script>

<template>
  <div class="ip-table-view">
    <IpResultsTable
      :data="appliedData"
      :enabled-fields="enabledFields"
      :pending-sideload="pendingSideload"
      :selection="sel"
      selectable
      cell-filters
      context-menu
      @filtered-change="handleFilteredChange"
      @apply-filter="onApplyFilter"
      @toggle-contains="onToggleContains"
      @remove-filter="onRemoveFilter"
    />
    <IpTableSidebar
      :data="filteredData"
      :is-field-enabled="fieldSettings.isFieldEnabled"
      :get-field-order="fieldSettings.getFieldOrder"
      :toggle-field="fieldSettings.toggleField"
      :set-field-order="fieldSettings.setFieldOrder"
      :reset-settings="fieldSettings.resetSettings"
      :has-custom-settings="fieldSettings.hasCustomSettings"
      :get-enabled-count="fieldSettings.getEnabledCount"
      :get-total-count="fieldSettings.getTotalCount"
      @export="handleExport"
    />
  </div>
</template>

<style scoped>
.ip-table-view {
  display: flex;
  height: 100%;
  width: 100%;
  gap: 0;
}
</style>
